


















































import { computed, defineComponent, onMounted, PropType, ref } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { getElement } from "@/lib/elementHelpers";
import { isAudioFile, isMediaFile } from "@/lib/fileHelpers";
import { ProcessedContentFiles } from "@/types";
import AttachmentsInfo from "@/components/AttachmentsInfo";
import AudioList from "@/components/AudioList";
import ContentFeedBaseView from "./components/ContentFeedBaseView";
import ContentFeedGallery from "./components/ContentFeedGallery";
import ContentFeedUnlockFeed from "./components/ContentFeedUnlockFeed";

export default defineComponent({
  name: "ContentFeedItem",
  components: {
    AudioList,
    AttachmentsInfo,
    ContentFeedBaseView,
    ContentFeedGallery,
    ContentFeedUnlockFeed,
  },
  props: {
    contentFeed: { type: Object as PropType<ContentFeed>, required: true },
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    showFollowButton: { type: Boolean, default: false },
    showReportItem: { type: Boolean, default: true },
    showComment: { type: Boolean, default: true },
    showReview: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: true },
    showShare: { type: Boolean, default: true },
    showPrice: { type: Boolean, default: true },
    showContentSales: { type: Boolean, default: false },
    pinnedPostId: { type: String as PropType<string | null>, default: null },
    enablePinPost: { type: Boolean, default: false },
    isFollowedUser: { type: Boolean },
    previewMode: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();
    const contentFeedItem = ref();

    const isAuthor = computed(() => !!authUser.value && props.contentFeed.userId === authUser.value.id);

    const isFreePost = computed(() => {
      return props.contentFeed.pricePackageId === "0" && props.contentFeed.subscriptionLevelId === "0";
    });

    const hasAccessToFile = (f: ContentFile) => {
      return isAuthor.value || props.contentFeed.purchased || f.publicAccess || isFreePost.value;
    };

    const processedFiles = computed(() => {
      return props.contentFeed.files.reduce<{
        documents: ProcessedContentFiles;
        media: ProcessedContentFiles;
        audio: ProcessedContentFiles;
      }>(
        (out, f) => {
          const key = isMediaFile(f) ? "media" : isAudioFile(f) ? "audio" : "documents";
          const accessKey = hasAccessToFile(f) ? "accessed" : "locked";
          out[key].all.push(f);
          out[key][accessKey].push(f);
          return out;
        },
        {
          documents: { locked: [], accessed: [], all: [] },
          media: { locked: [], accessed: [], all: [] },
          audio: { locked: [], accessed: [], all: [] },
        }
      );
    });

    const allLockedFiles = computed(() => {
      if (!processedFiles.value) return;
      return {
        mediaLocked: processedFiles.value.media.locked,
        audioLocked: processedFiles.value.audio.locked,
        documentsLocked: processedFiles.value.documents.locked,
      };
    });

    const showContentFeedUnlock = computed(() => {
      return !isAuthor.value && !props.contentFeed.user.disabled && !props.contentFeed.purchased;
    });

    const showFeedGallery = computed(() => {
      return (
        !!processedFiles.value &&
        (processedFiles.value.media.all.length > 0 ||
          processedFiles.value.documents.all.length > 0 ||
          processedFiles.value.audio.locked.length > 0)
      );
    });

    onMounted(() => emit("mounted", getElement(contentFeedItem.value)));

    return {
      contentFeedItem,
      processedFiles,
      allLockedFiles,
      showContentFeedUnlock,
      isAuthor,
      showFeedGallery,
    };
  },
});
